'use client';

import dynamic from 'next/dynamic';
import { useRef } from 'react';
import styled from 'styled-components';
import { Navigation } from 'swiper/modules';
import { Swiper, SwiperSlide } from 'swiper/react';

import { ArrowLeftSmall, ArrowRightSmall, PlayCircle } from '@zoomit/icons';
import { Image } from '@zoomit/primitives';
import { cssVar, theme } from '@zoomit/zoomit-ds';

import ProductDefaultCover from '../../assets/images/product-default-cover.webp';
import { useGallery } from '../../hooks/useGallery';
import { useOrigin } from '../../providers/OriginProvider';
import type { GalleryItem } from '../../types/shared';
import VideoCircle from '../video/VideoCircle';

//TODO: check bundle size. we wish just Gallery imported
const Gallery = dynamic(() => import('@zoomit/web-kit').then((mod) => mod.Gallery), {
  ssr: false,
});

type Props = {
  galleryItems: GalleryItem[];
  productId: string;
};

const ProductMediaPreviewMobile = (props: Props) => {
  const { galleryItems, productId } = props;

  const { handleCloseGallery, handleOpenGallery, isGalleryOpen } = useGallery({ galleryId: productId });

  const prevRef = useRef<HTMLDivElement>(null);
  const nextRef = useRef<HTMLDivElement>(null);

  const { baseMediaUrl } = useOrigin();

  if (galleryItems.length === 0)
    return (
      <Root>
        <ImageContainer>
          <Image
            src={ProductDefaultCover}
            fill
            alt="prview-default-cover-img"
            placeholder="blur"
            style={{
              objectFit: 'contain',
            }}
            sizes="calc(100vw - 16px)"
          />
        </ImageContainer>
      </Root>
    );

  const videos = galleryItems.filter((item) => item.type === 'Video');

  const firstVideoIndex = galleryItems.findIndex((item) => item.type === 'Video');

  const hasVideo = videos.length > 0;

  return (
    <Root>
      {hasVideo && (
        <div className="absolute top-2 right-2 z-10">
          <div className="relative h-16 w-16" onClick={() => handleOpenGallery(firstVideoIndex)}>
            <PlayCircle
              size={'lg'}
              fill={cssVar(theme.white)}
              className="absolute top-[50%] left-[50%] z-10 -translate-x-2/4 -translate-y-2/4"
            />
            <VideoCircle className={'h-16 w-16'} cover={videos[0].video!.coverImage} />
          </div>
        </div>
      )}
      <Swiper
        modules={[Navigation]}
        slidesPerView={1}
        spaceBetween={16}
        navigation={{
          prevEl: prevRef.current,
          nextEl: nextRef.current,
        }}
        loop
      >
        {galleryItems.map((media, index) => (
          <SwiperSlide key={index}>
            <ImageContainer>
              <Image
                src={
                  media.type === 'Image'
                    ? `${baseMediaUrl}/${media.image.id}`
                    : `${baseMediaUrl}/${media.video.coverImage.id}`
                }
                fill
                priority={index === 0}
                alt={media.type === 'Image' ? media.image.alt : media.video.coverImage.alt}
                fetchPriority={index === 0 ? 'high' : 'low'}
                placeholder={index === 0 ? 'blur' : 'empty'}
                blurDataURL={media.type === 'Image' ? media.image.preview : media.video.coverImage.preview}
                onClick={() => handleOpenGallery(index)}
                className="object-contain"
                sizes="calc(100vw - 16px)"
                quality={80}
              />
              {media.type === 'Video' && (
                <div
                  className={'translate absolute top-1/2 left-1/2 z-[9] -translate-x-1/2 -translate-y-1/2'}
                >
                  <PlayCircle size="xl" fill={cssVar(theme.white)} />
                </div>
              )}
            </ImageContainer>
          </SwiperSlide>
        ))}
        <ArrowWrapper ref={prevRef} direction="previous">
          <ArrowRightSmall fill={cssVar(theme.grey3)} size="sm" />
        </ArrowWrapper>
        <ArrowWrapper ref={nextRef} direction="next">
          <ArrowLeftSmall fill={cssVar(theme.grey3)} size="sm" />
        </ArrowWrapper>
      </Swiper>
      {isGalleryOpen && (
        <Gallery baseMediaUrl={baseMediaUrl} items={galleryItems} onCloseGallery={handleCloseGallery} />
      )}
    </Root>
  );
};

export default ProductMediaPreviewMobile;

const Root = styled.div`
  width: 100%;
  position: relative;
  overflow: hidden;

  img {
    cursor: pointer;
  }

  > .swiper {
    overflow-y: unset;
  }

  .swiper-pagination {
    position: absolute;
    z-index: 5;
    bottom: -2.4rem;
  }

  .swiper-pagination-bullet {
    background-color: var(${theme.grey5});
  }

  .swiper-pagination-bullet-active {
    background-color: var(${theme.primary1});
  }
`;

const ImageContainer = styled.div`
  aspect-ratio: 3/2;
  position: relative;
`;

const ArrowWrapper = styled.div<{ direction: 'next' | 'previous' }>`
  top: 50%;
  display: flex;
  align-items: center;
  width: 3.2rem;
  height: 3.2rem;
  transform: translateY(-50%);
  justify-content: ${({ direction }) => (direction === 'previous' ? 'flex-start' : 'flex-end')};
  left: ${({ direction }) => (direction === 'next' ? '0' : 'unset')};
  right: ${({ direction }) => (direction === 'previous' ? '0' : 'unset')};
  cursor: pointer;
  position: absolute;
  z-index: 999;
`;
